import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const { 
  FiPlus, FiEdit2, FiLogOut, FiUpload, FiBriefcase, FiMapPin, 
  FiCheckCircle, FiAlertCircle, FiLoader, FiArchive, FiCalendar, 
  FiImage, FiZap, FiRefreshCw, FiClock
} = FiIcons;

const Dashboard = () => {
  const { user, profile, jobs, signOut, updateProfile, uploadLogo, updateJobStatus, relistJob, fetchJobs, loading } = useAppContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isEditingName, setIsEditingName] = useState(false);
  const [newOrgName, setNewOrgName] = useState(profile?.org_name || '');
  const [isUploading, setIsUploading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (searchParams.get('payment') === 'success') {
      fetchJobs();
    }
  }, [searchParams, fetchJobs]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/employer/login');
  };

  const employerJobs = jobs.filter(j => j.employer_id === user?.id);
  
  const activeJobs = employerJobs.filter(j => 
    (j.status === 'pending') || 
    (j.status === 'active' && (!j.expires_at || new Date(j.expires_at) > new Date()))
  );

  const archivedJobs = employerJobs.filter(j => j.status === 'archived');
  const expiredJobs = employerJobs.filter(j => 
    j.status === 'active' && j.expires_at && new Date(j.expires_at) <= new Date()
  );

  const handleUpdateName = async () => {
    if (!newOrgName.trim()) return;
    await updateProfile({ org_name: newOrgName });
    setIsEditingName(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    const result = await uploadLogo(file);
    if (!result.success) alert(`Upload failed: ${result.error}`);
    setIsUploading(false);
  };

  const handleStatusChange = async (jobId, newStatus) => {
    const actionVerb = newStatus === 'archived' ? 'Archive' : 'Reactivate';
    if (!window.confirm(`${actionVerb} this job?`)) return;
    setActionLoading(jobId);
    const result = await updateJobStatus(jobId, newStatus);
    if (!result.success) alert(`${actionVerb} failed: ${result.error}`);
    setActionLoading(null);
  };

  const handleRelist = async (jobId) => {
    if (!window.confirm('This will reset your job to pending status for admin re-approval. Continue?')) return;
    setActionLoading(jobId);
    const result = await relistJob(jobId);
    if (!result.success) alert(`Relist failed: ${result.error}`);
    setActionLoading(null);
  };

  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    const diff = new Date(expiryDate) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading) return (
    <div className="flex-1 flex items-center justify-center bg-stone-50">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent shadow-emerald-500/10"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {searchParams.get('payment') === 'success' && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 p-6 bg-emerald-50 border border-emerald-100 rounded-[32px] flex items-center gap-4 text-emerald-800 shadow-sm">
            <div className="w-12 h-12 bg-emerald-500 rounded-2xl flex items-center justify-center text-white shrink-0 shadow-lg shadow-emerald-500/20"><SafeIcon icon={FiZap} className="text-xl" /></div>
            <div>
              <h4 className="font-black uppercase tracking-tight">Payment Successful!</h4>
              <p className="text-xs font-bold opacity-80 uppercase tracking-widest">Your featured job is being published.</p>
            </div>
            <button onClick={fetchJobs} className="ml-auto flex items-center gap-2 px-6 py-3 bg-white text-emerald-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition-all shadow-sm"><SafeIcon icon={FiRefreshCw} /> Refresh Status</button>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-[32px] shadow-sm border border-stone-200 p-8">
              <div className="flex flex-col items-center text-center">
                <div className="relative group mb-6 w-full flex justify-center">
                  <div className="min-h-[120px] max-w-full w-full bg-stone-50 rounded-2xl flex items-center justify-center overflow-hidden border-2 border-stone-100 group-hover:border-emerald-500 transition-all duration-500 p-4">
                    {profile?.logo_url ? <img src={profile.logo_url} alt="Logo" className="max-h-24 w-auto object-contain" /> : <div className="flex flex-col items-center gap-2"><SafeIcon icon={FiImage} className="text-4xl text-stone-300" /><span className="text-[10px] font-black uppercase text-stone-400 tracking-widest">No Logo</span></div>}
                    {isUploading && <div className="absolute inset-0 bg-white/90 flex items-center justify-center rounded-2xl"><SafeIcon icon={FiLoader} className="animate-spin text-emerald-600 text-xl" /></div>}
                  </div>
                  <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="absolute -bottom-2 -right-2 p-3 bg-stone-900 text-white rounded-2xl shadow-xl hover:bg-emerald-600 transition-all hover:scale-110 active:scale-95"><SafeIcon icon={FiUpload} className="text-sm" /></button>
                  <input type="file" ref={fileInputRef} onChange={handleLogoUpload} className="hidden" accept="image/*" />
                </div>

                {isEditingName ? (
                  <div className="w-full space-y-3">
                    <input type="text" value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} className="w-full px-4 py-3 bg-stone-50 border border-stone-200 rounded-xl text-center font-black text-stone-900 outline-none focus:ring-2 focus:ring-emerald-500 transition-all" autoFocus />
                    <div className="flex gap-2 justify-center"><button onClick={handleUpdateName} className="px-4 py-1.5 bg-emerald-600 text-white text-xs font-black uppercase tracking-widest rounded-lg hover:bg-emerald-700 transition-colors">Save</button><button onClick={() => setIsEditingName(false)} className="px-4 py-1.5 bg-stone-100 text-stone-500 text-xs font-black uppercase tracking-widest rounded-lg hover:bg-stone-200 transition-colors">Cancel</button></div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 group cursor-pointer" onClick={() => setIsEditingName(true)}>
                    <h2 className="text-2xl font-black text-stone-900 tracking-tight">{profile?.org_name || 'Employer Name'}</h2>
                    <SafeIcon icon={FiEdit2} className="text-stone-300 group-hover:text-emerald-600 text-sm transition-colors" />
                  </div>
                )}
                <p className="text-stone-500 text-sm font-bold mt-2 uppercase tracking-widest text-[10px]">{user?.email}</p>
              </div>
              <div className="mt-10 pt-8 border-t border-stone-50 space-y-2">
                <button onClick={handleSignOut} className="w-full flex items-center justify-center gap-3 px-4 py-4 text-red-600 hover:bg-red-50 rounded-2xl transition-all font-black text-xs uppercase tracking-[0.2em]"><SafeIcon icon={FiLogOut} /> Sign Out</button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-12">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-black text-stone-900 tracking-tighter uppercase">Jobs</h1>
                <p className="text-stone-500 font-bold uppercase tracking-widest text-[10px] mt-2">Manage your live and archived jobs</p>
              </div>
              <Link to="/employer/post-job" className="flex items-center gap-3 px-8 py-4 bg-stone-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-xl text-xs">
                <SafeIcon icon={FiPlus} /> Post a Job
              </Link>
            </div>

            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600"><SafeIcon icon={FiCheckCircle} /></div>
                <h2 className="text-xl font-black text-stone-900 tracking-tight uppercase">Active & Pending</h2>
              </div>
              <div className="grid gap-4">
                {activeJobs.length > 0 ? activeJobs.map(job => {
                  const daysLeft = getDaysRemaining(job.expires_at);
                  const locationList = job.locations?.map(l => l.name).join(', ') || 'Various Locations';
                  
                  return (
                    <motion.div key={job.id} layout className="group bg-white rounded-[32px] p-6 border-2 border-stone-100 hover:border-emerald-500/30 transition-all shadow-sm">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-black text-stone-900">{job.title}</h3>
                            {job.is_featured && <SafeIcon icon={FiZap} className="text-amber-500" />}
                            {job.status === 'pending' ? (
                              <span className="bg-amber-50 text-amber-700 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">Pending Approval</span>
                            ) : (
                              <span className="bg-emerald-50 text-emerald-700 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">Live</span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[10px] text-stone-500 font-black uppercase tracking-widest">
                            <Link to={`/employer/edit-job/${job.id}`} className="flex items-center gap-1 group/loc hover:text-emerald-600 transition-colors">
                              <SafeIcon icon={FiMapPin} className="text-emerald-500" /> 
                              <span className="truncate max-w-[200px]">{locationList}</span>
                              <SafeIcon icon={FiEdit2} className="text-[8px] opacity-0 group-hover/loc:opacity-100 transition-opacity" />
                            </Link>
                            {daysLeft !== null && (
                              <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} className="text-emerald-500" /> {daysLeft} Days Left</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link to={`/employer/edit-job/${job.id}`} className="w-10 h-10 flex items-center justify-center bg-stone-50 text-stone-400 hover:bg-stone-900 hover:text-white rounded-xl transition-all" title="Edit Job & Locations"><SafeIcon icon={FiEdit2} /></Link>
                          <button onClick={() => handleStatusChange(job.id, 'archived')} className="w-10 h-10 flex items-center justify-center bg-stone-50 text-stone-400 hover:bg-amber-500 hover:text-white rounded-xl transition-all" title="Archive"><SafeIcon icon={FiArchive} /></button>
                        </div>
                      </div>
                    </motion.div>
                  );
                }) : <div className="p-12 text-center bg-stone-100/50 rounded-[32px] border border-stone-200 border-dashed text-stone-400 font-bold uppercase tracking-widest text-[10px]">No active jobs.</div>}
              </div>
            </section>

            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-red-600"><SafeIcon icon={FiClock} /></div>
                <h2 className="text-xl font-black text-stone-900 tracking-tight uppercase text-red-900">Expired Jobs</h2>
              </div>
              <div className="grid gap-4">
                {expiredJobs.length > 0 ? expiredJobs.map(job => (
                  <div key={job.id} className="bg-white rounded-[32px] p-6 border-2 border-red-100 shadow-sm opacity-90">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-black text-stone-400">{job.title}</h3>
                          <span className="bg-red-50 text-red-700 text-[8px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">Expired</span>
                        </div>
                        <p className="text-[10px] text-stone-400 font-black uppercase tracking-widest">Job ended on {new Date(job.expires_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Link to={`/employer/edit-job/${job.id}`} className="w-10 h-10 flex items-center justify-center bg-stone-50 text-stone-400 hover:bg-stone-900 hover:text-white rounded-xl transition-all"><SafeIcon icon={FiEdit2} /></Link>
                        <button onClick={() => handleRelist(job.id)} disabled={actionLoading === job.id}
                          className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-black uppercase tracking-widest text-[10px] hover:bg-emerald-700 transition-all flex items-center gap-2">
                          {actionLoading === job.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <><SafeIcon icon={FiRefreshCw} /> Relist</>}
                        </button>
                      </div>
                    </div>
                  </div>
                )) : <div className="p-12 text-center bg-stone-100/10 rounded-[32px] text-stone-300 font-bold uppercase tracking-widest text-[10px]">No expired jobs.</div>}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;